/**
 * Defaults to format like "September 1, 2021"
 */
export const DATE_FORMAT_OPTIONS = {
  LONG: {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
  SHORT: {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  },
  WITH_TIME: {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  },
} as const;

export type DateFormat = keyof typeof DATE_FORMAT_OPTIONS;

/**
 * Formats a date using toLocaleDateString
 *
 * for additional options/formats, reference https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString
 */
export function formatDate(date?: Date, format: DateFormat = "LONG"): string {
  return new Date(date ?? new Date()).toLocaleDateString("en-US", DATE_FORMAT_OPTIONS[format]);
}

/**
 * JavaScript Pretty Date
 * Copyright (c) 2011 John Resig (ejohn.org)
 * Licensed under the MIT and GPL licenses.
 */
// TODO: fix plurals (eg "1 weeks ago")
export function prettyDate(time?: string | Date): string | null {
  const t: Date = typeof time === "string" ? new Date(time) : time ?? new Date();

  const diff = (new Date().getTime() - t.getTime()) / 1000;
  const dayDiff = Math.abs(Math.floor(diff / 86400));

  if (isNaN(dayDiff)) {
    return null;
  }

  if (diff < 0) {
    return (
      (dayDiff === -1 &&
        ((diff > -60 && "Seconds from now") ||
          (diff > -3600 && `${Math.floor(diff / -60)} minute${Math.floor(diff / -60) > 1 ? "s" : ""} from now`) ||
          (diff > -86400 && `${Math.floor(diff / -3600)} hour${Math.floor(diff / -3600) > 1 ? "s" : ""} from now`))) ||
      (dayDiff === -2 && "tomorrow") ||
      (dayDiff > -7 && dayDiff + " days from now") ||
      (dayDiff > -31 && Math.ceil(dayDiff / -7) + " weeks from now") ||
      null
    );
  }

  return (
    (dayDiff === 0 &&
      ((diff < 60 && "Just now") ||
        (diff < 120 && "One minute ago") ||
        (diff < 3600 && Math.floor(diff / 60) + " minutes ago") ||
        (diff < 7200 && "One hour ago") ||
        (diff < 86400 && Math.floor(diff / 3600) + " hours ago"))) ||
    (dayDiff === 1 && "Yesterday") ||
    (dayDiff < 7 && dayDiff + " days ago") ||
    Math.ceil(dayDiff / 7) + " weeks ago"
  );
}

export function formatTimeSnapshot(timestamp: Date | string) {
  if (typeof timestamp === "string") {
    timestamp = new Date(timestamp);
  }
  const localDate = timestamp
    .toLocaleDateString("en-US")
    .split("/")
    .map((s) => (s.length < 2 ? "0" + s : s))
    .join("/");
  const localTime = timestamp
    .toLocaleTimeString("en-US")
    .split(" ")
    .map((s, i) => (i === 0 ? s.slice(0, -3) : s))
    .join(" ");

  return { localDate, localTime };
}

function padTo2Digits(num: number) {
  return num.toString().padStart(2, "0");
}

export function formatYYYYMMDD(date = new Date()) {
  return [date.getFullYear(), padTo2Digits(date.getMonth() + 1), padTo2Digits(date.getDate())].join("");
}

/** Format a Date as YYYY-MM-DD in local time, so the calendar day matches what the user sees. */
export function toLocalDateString(date: Date): string {
  return [date.getFullYear(), padTo2Digits(date.getMonth() + 1), padTo2Digits(date.getDate())].join("-");
}

/** Today as YYYY-MM-DD in local time. */
export function todayDate(): string {
  return toLocalDateString(new Date());
}

/** YYYY-MM-DD in local time, `days` from today. Negative values look backwards. */
export function offsetDate(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toLocalDateString(date);
}

const iso8601FormatGroupedRegex = /^(\d{4})(\d{2})(\d{2})T?(\d{2})(\d{2})(\d{2})(\d{3})$/;
function isSevenNumbers(parts: number[]): parts is [number, number, number, number, number, number, number] {
  return parts.length === 7;
}

/**
 * Parse the ISO8601 format with optional T separator
 */
export function parseISO8601Format(date: string): Date | null {
  const matchResult = date.match(iso8601FormatGroupedRegex);
  if (matchResult) {
    const matched = Array.from(matchResult).slice(1).map(Number);
    if (isSevenNumbers(matched)) {
      matched[1] -= 1; // month is 0-indexed
      return new Date(Date.UTC(...matched));
    }
  }
  return null;
}

export function getRelativeTime(date: Date): string {
  const now = new Date();
  const ms = date.getTime();
  if (!Number.isFinite(ms)) {
    return "just now";
  }
  // Positive diffSec => future, negative => past
  const diffSec = Math.round((ms - now.getTime()) / 1000);
  const absSec = Math.abs(diffSec);

  const plural = (n: number, unit: string) => (n === 1 ? unit : `${unit}s`);
  const past = (n: number, unit: string) => `${n} ${plural(n, unit)} ago`;
  const future = (n: number, unit: string) => `in ${n} ${plural(n, unit)}`;
  const format = (n: number, unit: string) => (diffSec < 0 ? past(n, unit) : future(n, unit));

  if (absSec < 2) {
    return "just now";
  }
  if (absSec < 60) {
    return format(absSec, "second");
  }

  const absMin = Math.floor(absSec / 60);
  if (absMin < 60) {
    return format(absMin, "minute");
  }

  const absHr = Math.floor(absMin / 60);
  if (absHr < 24) {
    return format(absHr, "hour");
  }

  const absDay = Math.floor(absHr / 24);
  if (absDay < 7) {
    return format(absDay, "day");
  }

  if (absDay < 30) {
    const weeks = Math.floor(absDay / 7);
    return format(weeks, "week");
  }

  if (absDay < 365) {
    const months = Math.max(1, Math.floor(absDay / 30));
    return format(months, "month");
  }

  const years = Math.floor(absDay / 365);
  return format(years, "year");
}

/** Formats a millisecond duration into a compact human-readable string (e.g. "3m 12s"). */
export function prettyTime(ms: number): string {
  if (ms < 1000) {
    return "<1s";
  }
  const seconds = Math.floor(ms / 1000) % 60;
  const minutes = Math.floor(ms / 60000) % 60;
  const hours = Math.floor(ms / 3600000);
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}
